import { useEffect } from 'react';
import uniqBy from 'lodash/uniqBy';
import type { QueryResult } from '@apollo/react-common';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { type IssueId, type ProjectId, toIssueKey, toIssueId } from '@atlassian/jira-shared-types';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types';
import type { IssueMetrics, IssueTableData } from '../../common/types';
import { getIssueUrl } from '../../common/utils/issues';
import type {
	jira_cycleTimeReport_issuesQueryByJql_Query as IssueQuery,
	jira_cycleTimeReport_issuesQueryByJql_QueryVariables as IssueQueryVariables,
} from './__generated_apollo__/jira_cycleTimeReport_issuesQueryByJql_Query';
import { ISSUES_QUERY_BY_JQL } from './gql';
import type { FetchMoreFn } from './types';

export const MAX_ISSUES_PER_PAGE = 100;

export const buildVariables = (
	issueIds: IssueId[],
	projectId: ProjectId | null,
	after: string | null,
): IssueQueryVariables => ({
	// Do not change this over 100. Gira's issue query only supports up to 100.
	first: MAX_ISSUES_PER_PAGE,
	after,
	jql: projectId == null ? '' : `project=${projectId} AND id IN (${issueIds.join(',')})`,
});

export const getIssueIdsFromIssueMetrics = (issueMetrics?: IssueMetrics[]): IssueId[] =>
	issueMetrics ? issueMetrics.map((x) => x.id) : [];

export const useAutoPagination = (
	fetchMore: FetchMoreFn,
	after: string | null | null | undefined,
	perIssueMetricsData: IssueMetrics[] | null | undefined,
	projectId: ProjectId | null,
	isQueryLoading: boolean,
) => {
	useEffect(() => {
		if (after != null && isQueryLoading === false) {
			fetchMore({
				query: ISSUES_QUERY_BY_JQL,
				variables: buildVariables(
					// @ts-expect-error - TS2345 - Argument of type 'IssueMetrics[] | null | undefined' is not assignable to parameter of type 'IssueMetrics[] | undefined'.
					getIssueIdsFromIssueMetrics(perIssueMetricsData),
					projectId,
					after,
				),
				updateQuery: (prevResult, { fetchMoreResult }) => {
					if (fetchMoreResult == null || fetchMoreResult.issues == null) {
						return prevResult;
					}

					const { issues: fetchMoreResultIssues } = fetchMoreResult;

					const prevNodes = prevResult.issues?.nodes ?? [];
					const fetchMoreNodes = fetchMoreResultIssues.nodes ?? [];
					const newNodes = uniqBy(
						[...prevNodes, ...fetchMoreNodes],
						(issueNode) => issueNode?.id ?? '',
					);

					return {
						issues: {
							...(prevResult.issues ?? { __typename: 'GQLIssueConnection' }),
							nodes: newNodes,
							pageInfo: fetchMoreResultIssues.pageInfo,
						},
					};
				},
			});
		}
	}, [after, perIssueMetricsData, projectId, isQueryLoading, fetchMore]);
};

export const getValidatedData = (
	queryResult: QueryResult<IssueQuery, IssueQueryVariables>,
	issueMetrics: IssueMetrics[],
	cycleTimeStatistic: number | null,
): IssueTableData => {
	const issueNodes = queryResult.data?.issues?.nodes;

	if (issueNodes == null) {
		throw Error(
			`Cycle time report: issueQuery's nodes is missing.${
				queryResult.error ? ` Query error msg: ${queryResult.error.message}` : ''
			}`,
		);
	}

	const issueNodeIdToMetricsMap = new Map<string, IssueMetrics>();
	issueMetrics.forEach((x) => issueNodeIdToMetricsMap.set(x.id, x));

	return {
		cycleTimeStatistic,
		rows: issueNodes.flatMap((issueNode) => {
			const issueKeyRaw = issueNode?.issuekey?.stringValue;
			const issueSummary = issueNode?.summary?.textValue;
			const issueIdRaw = issueNode?.issueId;
			let issueTypeIconUrl = issueNode?.issuetype?.iconUrl;

			if (issueNode == null || issueKeyRaw == null || issueSummary == null || issueIdRaw == null) {
				throw new Error(
					`Cycle time report: issue query's node or its prop is missing.${
						queryResult.error ? ` Query error: ${queryResult.error.message}` : ''
					}`,
				);
			}

			// If icon url is missing, we can display broken image rather than blowing up the whole page
			issueTypeIconUrl = issueTypeIconUrl ?? '';
			const issueKey = toIssueKey(issueKeyRaw);
			const issueId = toIssueId(`${issueIdRaw}`);
			const metrics: IssueMetrics | undefined = issueNodeIdToMetricsMap.get(issueId);
			if (metrics === undefined) {
				fireErrorAnalytics({
					error: new Error(`Couldn't find issue with issue id ${issueId} in perIssueMetrics data`),
					meta: {
						id: 'issueTableServiceTransformer',
						packageName: 'jiraSoftwareCycleTimeBreakdownReport',
						teamName: 'fusion-solaris',
					},
				});
				return [];
			}

			return [
				{
					cycleTime: metrics.totalCycleTimeSeconds,
					issue: {
						id: issueId,
						key: issueKey,
						typeIconUrl: issueTypeIconUrl,
						summary: issueSummary,
						link: getIssueUrl(issueKey),
					},
					numCommits: metrics.commitsCount,
					numPullRequests: metrics.pullRequestsCount,
					reviewTime: metrics.meanReviewTimeSeconds,
					dateDeployed: metrics.lastSuccessfulProductionDeployment,
				},
			] as const;
		}),
	};
};

export const transformResult = (
	queryResult: QueryResult<IssueQuery, IssueQueryVariables>,
	issueMetrics: IssueMetrics[],
	cycleTimeStatistics: number | null,
): ServiceQueryResultWithFetch<
	IssueTableData,
	QueryResult<IssueQuery, IssueQueryVariables>['refetch']
> => {
	const resultBase = {
		loading: queryResult.loading,
		error: queryResult.error,
		fetch: queryResult.refetch,
	};

	if (queryResult.loading) {
		return resultBase;
	}

	try {
		return {
			...resultBase,
			data: getValidatedData(queryResult, issueMetrics, cycleTimeStatistics),
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return {
			...resultBase,
			error,
		};
	}
};
